import createExternalStore from "../../../common/utils/createExternalStore";
import { validToken } from "../../../helpers/jwt";
import { sleep } from "../../../helpers/sleep";
import type { AuthenticatedAccountUser } from "../../../types/management-auth";
import { authenticate, logout, refresh } from "../../auth";
import {
    fetchAccountToken,
    fetchAccountUser,
    fetchExchangeToken,
    fetchPostPassword,
    fetchPutMfaSms,
    fetchPutMfaTotp,
} from "../../dinteroAuthApi";
import {
    clearPartner,
    getPartner,
    setCognitoSession,
    setLastAccountId,
    setPartner,
} from "../../localStorage";
import type {
    AuthenticatedUser,
    ChangeMfaSmsModalStates,
    ChangeMfaTotpModalStates,
    ChangePasswordModalStates,
    CognitoSession,
    PartnerUser,
} from "../../types";
import { isExchangeToken, refreshExchangeToken } from "../exchangeToken";

export interface State extends AuthenticatedUser {
    tokens: {
        [accountId: string]: string;
    };
    isLoadingTokens: boolean;
    isLoadingSubAccountTokens: boolean;
    exchangeTokenPartnerId: string | undefined;
    seq: number;
    changePasswordState: ChangePasswordModalStates;
    changeMfaSmsState: ChangeMfaSmsModalStates;
    changeMfaTotpState: ChangeMfaTotpModalStates;
    partner_user: PartnerUser | undefined;
}

const initialState: State = {
    cognito_access_token: "",
    exchangeTokenPartnerId: undefined,
    account_user: {
        id: "",
        accounts: [],
    },
    tokens: {},
    isLoadingTokens: false,
    isLoadingSubAccountTokens: false,
    seq: 0,
    changePasswordState: "pristine",
    changeMfaSmsState: "pristine",
    changeMfaTotpState: "pristine",
    partner_user: undefined,
};

const authStore = createExternalStore(
    initialState,
    {
        setAuthenticatedUser: (
            state,
            action: {
                accountUser: AuthenticatedUser;
                exchangeTokenPartnerId?: string;
            },
        ) => {
            const [partner_user, exchangeTokenPartnerId] = getPartner();
            logoutOnExpiry();
            return {
                ...state,
                ...action.accountUser,
                partner_user,
                exchangeTokenPartnerId,
                isLoadingTokens: true,
            };
        },
        setCognitoSession: (
            state,
            action: {
                cognitoSession: CognitoSession;
            },
        ) => {
            // persist updated cognito session in local storage
            setCognitoSession(action.cognitoSession);
            return {
                ...state,
                ...action.cognitoSession,
            };
        },
        setPartner: (
            state,
            action: {
                partner: PartnerUser;
            },
        ) => {
            setPartner(action.partner, state.exchangeTokenPartnerId as string);
            return {
                ...state,
                partner_user: action.partner,
            };
        },
        setInspectAuthenticatedUser: (
            state,
            action: {
                accountUser: AuthenticatedUser;
                exchangeTokenPartnerId: string;
            },
        ) => {
            setPartner(state, action.exchangeTokenPartnerId);
            return {
                ...state,
                ...action.accountUser,
                // The active session is the exchange token; its refresh token
                // and config belong to the partner, so keep them only on
                // `partner_user` to avoid refreshing into the partner identity.
                cognito_refresh_token: undefined,
                cognito_config: undefined,
                exchangeTokenPartnerId: action.exchangeTokenPartnerId,
                partner_user: { ...state },
                tokens: {},
            };
        },
        setLeaveInspect: (state) => {
            if (state.partner_user) {
                clearPartner();
                setCognitoSession({
                    cognito_access_token:
                        state.partner_user.cognito_access_token,
                    cognito_config: state.partner_user.cognito_config,
                    cognito_refresh_token:
                        state.partner_user.cognito_refresh_token,
                });
            }
            return {
                ...state,
                ...(state.partner_user || {}),
                partner_user: undefined,
                exchangeTokenPartnerId: undefined,
            };
        },
        updateAuthenticatedUser: (
            state,
            action: {
                accountId: string;
            },
        ) => {
            refetchAuthenticatedUser(state, action.accountId);
            return {
                ...state,
            };
        },
        updateAuthenticatedUserResult: (
            state,
            action: {
                authenticatedUser: AuthenticatedUser;
                accountId: string;
            },
        ) => {
            refetchTokens(action.accountId);
            return {
                ...state,
                ...action.authenticatedUser,
            };
        },
        loadTokens: (
            state,
            action: {
                accountId: string;
                showLoading: boolean;
            },
        ) => {
            fetchAccountTokens(state, action.accountId);
            return {
                ...state,
                isLoadingTokens: action.showLoading,
                seq: state.seq + 1,
            };
        },
        loadTokensResponse: (
            state,
            action: {
                accounts: {
                    accountId: string;
                    token: string;
                }[];
            },
        ) => {
            return {
                ...state,
                isLoadingTokens: false,
                tokens: {
                    ...state.tokens,
                    ...action.accounts.reduce(
                        (acc, x) => ({ ...acc, [x.accountId]: x.token }),
                        {},
                    ),
                },
            };
        },
        loadSubAccountTokens: (
            state,
            action: {
                accountId: string;
                subAccountId: string;
            },
        ) => {
            fetchSubAccountTokens(action.accountId, action.subAccountId);
            return {
                ...state,
                isLoadingSubAccountTokens: true,
            };
        },
        loadSubAccountTokensResponse: (
            state,
            action: {
                accounts: {
                    accountId: string;
                    token: string;
                }[];
            },
        ) => {
            return {
                ...state,
                isLoadingSubAccountTokens: false,
                tokens: {
                    ...state.tokens,
                    ...action.accounts.reduce(
                        (acc, x) => ({ ...acc, [x.accountId]: x.token }),
                        {},
                    ),
                },
            };
        },
        openChangePassword: (state) => {
            return {
                ...state,
                changePasswordState: "pristine",
            };
        },
        changePassword: (
            state,
            action: {
                previous_password: string;
                proposed_password: string;
            },
        ) => {
            handleChangePassword(state, {
                previous_password: action.previous_password,
                proposed_password: action.proposed_password,
            });
            return {
                ...state,
                changePasswordState: "in-flight",
            };
        },
        changePasswordResponse: (
            state,
            action: {
                result: ChangePasswordModalStates;
            },
        ) => {
            return {
                ...state,
                changePasswordState: action.result,
            };
        },
        openAuthenticatedUserSettings: (state) => {
            return {
                ...state,
                changeMfaSmsState: "pristine",
                changeMfaTotpState: "pristine",
            };
        },
        changeAuthenticatedUserMfaSms: (
            state,
            action: {
                enabled: boolean;
                phone_number?: string;
            },
        ) => {
            handleChangeMfaSms(state, {
                enabled: action.enabled,
                phone_number: action.phone_number,
            });
            return {
                ...state,
                changeMfaSmsState: "in-flight",
            };
        },
        changeAuthenticatedUserMfaSmsResponse: (
            state,
            action: {
                result: ChangeMfaSmsModalStates;
            },
        ) => {
            return {
                ...state,
                changeMfaSmsState: action.result,
            };
        },
        changeAuthenticatedUserMfaTotp: (
            state,
            action: {
                enabled: boolean;
                user_code?: string;
            },
        ) => {
            handleChangeMfaTotp(state, {
                enabled: action.enabled,
                user_code: action.user_code,
            });
            return {
                ...state,
                changeMfaTotpState: "in-flight",
            };
        },
        changeAuthenticatedUserMfaTotpResponse: (
            state,
            action: {
                result: ChangeMfaTotpModalStates;
            },
        ) => {
            return {
                ...state,
                changeMfaTotpState: action.result,
            };
        },
        setState: (state, action: Partial<State>) => {
            return {
                ...state,
                ...action,
            };
        },
    },
    "AUTH",
);

const getAccountIdsForAccount = (
    accountUser: AuthenticatedAccountUser,
    accountId: string | undefined,
) => {
    const withoutPrefix = accountId?.substring(1);
    const matchingAccounts = accountUser.accounts.filter(
        (a) => a.account_id.substring(1) === withoutPrefix,
    );
    return matchingAccounts.map((a) => a.account_id);
};

export const getCognitoToken = async () => {
    // Get cognito token for current account.
    // If a user is inspecting, the token returned is the exchange token.
    const cognitoSession: CognitoSession = authStore.select((state) => ({
        cognito_access_token: state.cognito_access_token,
        cognito_refresh_token: state.cognito_refresh_token,
        cognito_config: state.cognito_config,
    }));
    const validCognitoToken = validToken(cognitoSession.cognito_access_token);
    if (validCognitoToken) {
        return validCognitoToken;
    }

    // The cognito access token is invalid or expired. If it is an inspect
    // exchange token, refresh it via the partner session it was minted from.
    const partnerSession = authStore.select((state) => state.partner_user);
    if (isExchangeToken(cognitoSession.cognito_access_token, partnerSession)) {
        const result = await refreshExchangeToken(
            cognitoSession.cognito_access_token,
            partnerSession as PartnerUser,
        );
        if (result) {
            if (result.partnerSession) {
                // persist the refreshed partner cognito token
                authStore.dispatch("setPartner", {
                    partner: result.partnerSession,
                });
            }
            authStore.dispatch("setCognitoSession", {
                cognitoSession: {
                    ...cognitoSession,
                    cognito_access_token: result.accessToken,
                },
            });
            return result.accessToken;
        }
        // The exchange token could not be refreshed. Do NOT fall back to a
        // regular cognito refresh: the refresh token/config in the session
        // belong to the partner, not the inspected account, so using them
        // would silently swap the user onto their partner identity.
        logout(false);
        return;
    }

    // Update regular (not inspect) cognito token using the refresh token
    const updatedSession = await refresh(cognitoSession);
    if (updatedSession) {
        // persist updated session in authStore
        authStore.dispatch("setCognitoSession", {
            cognitoSession: updatedSession,
        });
        return updatedSession.cognito_access_token;
    }
    // log out user when we do not have a cognito token or a valid refresh token
    logout(false);
};

let isChecking = false;
const logoutOnExpiry = async () => {
    // only run one instance of the checker.
    if (isChecking) {
        return;
    }
    isChecking = true;
    // Checks every minute
    while (isChecking) {
        await sleep(60 * 1000);

        // getCognitoToken() logs out the user when we no longer have a valid cognito access token.
        const token = await getCognitoToken();
        if (!token) {
            // Reset expiry logger sot we can restart when
            isChecking = false;
        }
    }
};

const fetchAccountTokens = async (state: State, accountId: string) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const accountIds = getAccountIdsForAccount(
            state.account_user,
            accountId,
        );
        const promises = accountIds.map((id) =>
            fetchAccountToken(cognitoToken, id),
        );
        const responses = await Promise.all(promises);
        const accounts = responses.filter((r) => r) as {
            accountId: string;
            token: string;
        }[];
        setLastAccountId(accountId);
        authStore.dispatch("loadTokensResponse", {
            accounts,
        });
    }
};

const refetchAuthenticatedUser = async (state: State, accountId: string) => {
    // User has been updated so we fetch it again to get newest state
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const authenticatedAccountUser = await fetchAccountUser(cognitoToken);
        if (authenticatedAccountUser) {
            authStore.dispatch("updateAuthenticatedUserResult", {
                authenticatedUser: {
                    account_user: authenticatedAccountUser,
                } as AuthenticatedUser,
                accountId,
            });
        } else {
            // unable to authenticate user so we force a logout so the user can login again
            logout(false);
        }
    }
};

const refetchTokens = async (accountId: string) => {
    // TODO: figure out what this is needed for?
    return authStore.dispatch("loadTokens", {
        accountId,
        showLoading: false,
    });
};

const fetchSubAccountTokens = async (
    accountId: string,
    subAccountId: string,
) => {
    // When a partner is looking at details of a sub account from partner admin, but is not inspecting
    const exchangeTokenResponse = await fetchExchangeToken(
        accountId,
        subAccountId,
    );
    if (!exchangeTokenResponse) {
        return authStore.dispatch("loadSubAccountTokensResponse", {
            accounts: [],
        });
    }
    const nonPrefixedSubAccount = subAccountId.substring(1);
    const promises = ["P", "T"]
        .map((prefix) => `${prefix}${nonPrefixedSubAccount}`)
        .map((id) => fetchAccountToken(exchangeTokenResponse.access_token, id));
    const responses = await Promise.all(promises);
    const accounts = responses.filter((r) => r) as {
        accountId: string;
        token: string;
    }[];
    authStore.dispatch("loadSubAccountTokensResponse", {
        accounts,
    });
};

const handleChangePassword = async (
    state: State,
    payload: {
        previous_password: string;
        proposed_password: string;
    },
) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const response = await fetchPostPassword(cognitoToken, payload);
        switch (response.status) {
            case 201:
                return dispatchPasswordResult("success");
            case 400:
                return dispatchPasswordResult("wrong_password");
            case 401:
                return dispatchPasswordResult("error");
            case 403:
                return dispatchPasswordResult("error");
            case 429:
                return dispatchPasswordResult("rate_limit");
            case 500:
                return dispatchPasswordResult("error");
            default:
                return dispatchPasswordResult("error");
        }
    }
};

const dispatchPasswordResult = (
    result: "success" | "wrong_password" | "error" | "rate_limit",
) =>
    authStore.dispatch("changePasswordResponse", {
        result,
    });

const handleChangeMfaSms = async (
    state: State,
    payload: {
        enabled: boolean;
        phone_number?: string;
    },
) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const response = await fetchPutMfaSms(
            state.cognito_access_token,
            payload,
        );
        switch (response.status) {
            case 201:
                return dispatchMfaSmsResult("success");
            case 400:
                return dispatchMfaSmsResult("invalid_phone");
            case 401:
                return dispatchMfaSmsResult("error");
            case 403:
                return dispatchMfaSmsResult("error");
            case 429:
                return dispatchMfaSmsResult("rate_limit");
            case 500:
                return dispatchMfaSmsResult("error");
            default:
                return dispatchMfaSmsResult("error");
        }
    }
};

const dispatchMfaSmsResult = (
    result: "success" | "invalid_phone" | "error" | "rate_limit",
) =>
    authStore.dispatch("changeAuthenticatedUserMfaSmsResponse", {
        result,
    });

const handleChangeMfaTotp = async (
    state: State,
    payload: {
        enabled: boolean;
        user_code?: string;
    },
) => {
    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        const response = await fetchPutMfaTotp(
            state.cognito_access_token,
            payload,
        );
        switch (response.status) {
            case 201:
                return dispsatchMfaTotpResult("success");
            case 400:
                return dispsatchMfaTotpResult("invalid_user_code");
            case 401:
                return dispsatchMfaTotpResult("error");
            case 403:
                return dispsatchMfaTotpResult("error");
            case 429:
                return dispsatchMfaTotpResult("rate_limit");
            case 500:
                return dispsatchMfaTotpResult("error");
            default:
                return dispsatchMfaTotpResult("error");
        }
    }
};

const dispsatchMfaTotpResult = (
    result: "success" | "invalid_user_code" | "error" | "rate_limit",
) =>
    authStore.dispatch("changeAuthenticatedUserMfaTotpResponse", {
        result,
    });

// External functions

// Used by api.ts, apiRequest.ts, fulfill.ts to get access token for a given API request
export const getAuthTokenForAccount = async (accountId: string) => {
    const tokens = authStore.select((state) => state.tokens);
    const tokenFromStore = validToken(tokens[accountId]);
    if (tokenFromStore) {
        return tokenFromStore;
    }
    // authStore token for the account is invalid or expired
    // check if cognito token is still valid

    const cognitoToken = await getCognitoToken();
    if (cognitoToken) {
        // reload account token using the cognito token
        const account = await fetchAccountToken(cognitoToken, accountId);
        if (account) {
            authStore.dispatch("loadTokensResponse", {
                accounts: [account],
            });
            return account.token;
        }
    }
};

export const inspectAccount = async (
    accountId: string,
    subAccountId: string,
) => {
    const result = await fetchExchangeToken(accountId, subAccountId);
    if (result) {
        const authenticatedUser = await authenticate({
            cognito_access_token: result.access_token,
        });
        if (authenticatedUser) {
            authStore.dispatch("setInspectAuthenticatedUser", {
                accountUser: authenticatedUser,
                exchangeTokenPartnerId: accountId,
            });
        }
    }
};

export default authStore;
